import { createContext, useContext, useState, useCallback, useEffect } from 'react';

const AppContext = createContext(null);

export const ROLES = {
  ADMIN: 'admin',
  SUPREME_MANAGER: 'supreme_manager',
  MANAGER: 'manager',
  QUALITY_CHECKER: 'quality_checker',
  FIELD_EXECUTIVE: 'field_executive',
  VENDOR: 'vendor',
  CLIENT: 'client',
  BUSINESS_PARTNER: 'business_partner',
  UNIVERSAL_QC: 'universal_qc',
  DATA_ENTRY_OPERATOR: 'data_entry_operator',
  VENDOR_FE: 'vendor_fe',
  ADMIN_CAPS: 'admin',
};

export const ROLE_LABELS = {
  [ROLES.ADMIN]: 'Admin',
  [ROLES.SUPREME_MANAGER]: 'Supreme Manager',
  [ROLES.MANAGER]: 'Manager',
  [ROLES.QUALITY_CHECKER]: 'Quality Checker',
  [ROLES.FIELD_EXECUTIVE]: 'Field Executive',
  [ROLES.VENDOR]: 'Vendor',
  [ROLES.CLIENT]: 'Client',
  [ROLES.BUSINESS_PARTNER]: 'Business Partner',
  [ROLES.UNIVERSAL_QC]: 'Universal QC',
  [ROLES.DATA_ENTRY_OPERATOR]: 'Data Entry Operator',
  [ROLES.VENDOR_FE]: 'Vendor Side Field Executive',
};

export const ROLE_HIERARCHY = {
  [ROLES.ADMIN]: [ROLES.SUPREME_MANAGER, ROLES.MANAGER, ROLES.QUALITY_CHECKER, ROLES.FIELD_EXECUTIVE, ROLES.VENDOR, ROLES.CLIENT, ROLES.BUSINESS_PARTNER, ROLES.UNIVERSAL_QC, ROLES.DATA_ENTRY_OPERATOR, ROLES.VENDOR_FE],
  [ROLES.SUPREME_MANAGER]: [ROLES.MANAGER, ROLES.QUALITY_CHECKER, ROLES.FIELD_EXECUTIVE, ROLES.VENDOR, ROLES.VENDOR_FE],
  [ROLES.MANAGER]: [ROLES.FIELD_EXECUTIVE, ROLES.VENDOR, ROLES.VENDOR_FE],
  [ROLES.VENDOR]: [ROLES.VENDOR_FE],
  [ROLES.QUALITY_CHECKER]: [],
  [ROLES.FIELD_EXECUTIVE]: [],
  [ROLES.CLIENT]: [],
  [ROLES.BUSINESS_PARTNER]: [ROLES.CLIENT],
  [ROLES.UNIVERSAL_QC]: [],
  [ROLES.DATA_ENTRY_OPERATOR]: [],
  [ROLES.VENDOR_FE]: [],
};

// GLOBAL DEFAULTS - Overridden by user specific permissions
const DEFAULT_ROLE_PERMISSIONS = {
  [ROLES.ADMIN]: {
    dashboard: true,
    cases: { view: true, create: true, edit: true, delete: true, assign: true },
    users: { view: true, 'see list': true, create: true, modify: true, delete: true, deactivate: true, blocked: true },
    clients: { view: true, create: true, modify: true, delete: true, blocked: true },
    products: { view: true, create: true, edit: true, delete: true },
    qc: { view: true, 'create lead': true, 'assign lead': true, 'verify lead': true },
    reports: { view: true, 'download report and zip case wise only': true, analytics: true },
    invoices: { view: true, create: true, approve: true },
    penalties: { view: true, create: true, approve: true, reject: true },
    payouts: { view: true, 'generate request': true, 'sent request': true, approve: true, reject: true, modify: true },
    formBuilder: true,
    accessControl: true,
    statusTracker: true,
    notifications: { view: true, send: true },
    vendorCreation: { view: true, create: true, delete: true, blocked: true, modify: true },
    manualGeo: { view: true, create: true, edit: true, delete: true },
    manualVerification: { view: true, create: true, edit: true, delete: true },
    qualityCheck: { view: true, create: true, edit: true, delete: true },
  },
  [ROLES.SUPREME_MANAGER]: {
    dashboard: true,
    cases: { view: true, create: true, edit: true, delete: true, assign: true },
    users: { view: true, 'see list': true, create: true, modify: true, delete: false, deactivate: true, blocked: true },
    clients: { view: true, create: false, modify: true, delete: false, blocked: true },
    products: { view: true, create: false, edit: false, delete: false }, // View Product & Verification
    qc: { view: true, 'create lead': true, 'assign lead': true, 'verify lead': true },
    reports: { view: true, 'download report and zip case wise only': true, analytics: true },
    invoices: false, // REMOVED
    penalties: false, // REMOVED
    payouts: { view: true, 'generate request': true, 'sent request': true, approve: true, reject: false, modify: true },
    formBuilder: true,
    accessControl: false,
    statusTracker: true,
    notifications: { view: true, send: true },
    vendorCreation: { view: true, create: true, delete: false, blocked: true, modify: true },
    manualGeo: { view: true, create: true, edit: true, delete: false },
    manualVerification: { view: true, create: true, edit: true, delete: false },
    qualityCheck: { view: true, create: true, edit: true, delete: false },
  },
  [ROLES.MANAGER]: {
    dashboard: true,
    cases: { view: true, create: true, edit: true, delete: false, assign: true },
    users: { view: true, 'see list': true, create: true, modify: true, delete: false, allowVendorFeOnly: true, deactivate: false, blocked: false },
    clients: { view: true, create: false, modify: false, delete: false, blocked: false },
    products: false, // REMOVED
    qc: { view: true, 'create lead': false, 'assign lead': false, 'verify lead': false },
    reports: { view: true, 'download report and zip case wise only': true, analytics: false }, // ALLOWED
    invoices: false, // REMOVED
    penalties: { view: true, create: true, approve: false }, // Can Set & Raise Waiver, Cannot Approve
    payouts: { view: true, 'generate request': true, 'sent request': true, approve: true, reject: false, modify: false },
    formBuilder: false,
    accessControl: false,
    statusTracker: true,
    notifications: { view: true, send: false },
    vendorCreation: { view: true, create: false, delete: false, blocked: false, modify: false },
    manualGeo: { view: true, create: false, edit: false, delete: false },
    manualVerification: { view: true, create: false, edit: false, delete: false },
    qualityCheck: { view: true, create: false, edit: false, delete: false },
  },
  [ROLES.QUALITY_CHECKER]: {
    dashboard: true,
    cases: false, // REMOVED
    users: false,
    clients: false,
    products: false,
    qc: { view: true, 'create lead': true, 'assign lead': true, 'verify lead': true },
    reports: false, // REMOVED
    invoices: false,
    penalties: { view: true, create: true, approve: false }, // Can Raise
    payouts: false,
    formBuilder: false,
    accessControl: false,
    statusTracker: false, // REMOVED
    notifications: { view: true, send: false },
    vendorCreation: false,
    manualGeo: false,
    manualVerification: false,
    qualityCheck: { view: true, create: true, edit: true, delete: false },
  },
  [ROLES.FIELD_EXECUTIVE]: {
    dashboard: true,
    cases: false,
    users: false,
    clients: false,
    products: false,
    qc: false,
    reports: false,
    invoices: false,
    penalties: { view: true },
    payouts: { view: true, 'generate request': true }, // Generate payouts
    formBuilder: false,
    accessControl: false,
    statusTracker: false,
    notifications: false,
    vendorCreation: false,
    manualGeo: false,
    manualVerification: { view: true, create: true, edit: false, delete: false },
    qualityCheck: false,
  },
  [ROLES.VENDOR]: {
    dashboard: true,
    cases: { view: true, create: false, edit: false, delete: false, assign: true },
    users: false,
    clients: false,
    products: false,
    qc: false,
    reports: false,
    invoices: false,
    penalties: { view: true, create: true, approve: true }, // Penalty management enabled
    payouts: { view: true, 'generate request': true }, // Monthly payout request
    formBuilder: false,
    accessControl: false,
    statusTracker: false, // HIDDEN
    notifications: { view: true, send: false },
    vendorCreation: false,
    manualGeo: false, // HIDDEN
    manualVerification: { view: true, create: false, edit: false, delete: false },
    qualityCheck: false,
  },
  [ROLES.CLIENT]: {
    dashboard: true,
    cases: { view: true, create: true, edit: false, delete: false, assign: false },
    users: false,
    clients: false,
    products: false, // REMOVED
    qc: false,
    reports: { view: true, 'download report and zip case wise only': true, analytics: false },
    invoices: false, // REMOVED
    penalties: false,
    payouts: false,
    formBuilder: false,
    accessControl: false,
    statusTracker: true,
    notifications: { view: true, send: false },
    vendorCreation: false,
    manualGeo: false,
    manualVerification: false,
    qualityCheck: false,
  },
  [ROLES.BUSINESS_PARTNER]: {
    dashboard: true,
    cases: false,
    users: false,
    clients: { view: true, create: true, modify: false, delete: false, blocked: false }, // Client Creation and Activity
    products: false,
    qc: false,
    reports: false,
    invoices: false,
    penalties: false,
    payouts: { view: true, 'generate request': true }, // Add / Track commission
    formBuilder: false,
    accessControl: false,
    statusTracker: false,
    notifications: false,
    vendorCreation: false,
    manualGeo: false,
    manualVerification: false,
    qualityCheck: false,
  },
  [ROLES.UNIVERSAL_QC]: {
    dashboard: true,
    cases: { view: true },
    users: false,
    clients: false,
    products: false,
    qc: { view: true, 'create lead': true, 'assign lead': true, 'verify lead': true, allCases: true }, // All hierarchy
    reports: false,
    invoices: false,
    penalties: false,
    payouts: false,
    formBuilder: false,
    accessControl: false,
    statusTracker: false,
    notifications: { view: true, send: false },
    vendorCreation: false,
    manualGeo: false,
    manualVerification: false,
    qualityCheck: { view: true, create: true, edit: true, delete: false },
  },
  [ROLES.DATA_ENTRY_OPERATOR]: {
    dashboard: true,
    cases: false, // REMOVED
    users: false,
    clients: false,
    products: false,
    qc: false,
    reports: false,
    invoices: false,
    penalties: false,
    payouts: false,
    formBuilder: false,
    accessControl: false,
    statusTracker: false, // REMOVED
    notifications: { view: true, send: false },
    vendorCreation: false,
    manualGeo: false,
    manualVerification: false,
    qualityCheck: false,
  },
  [ROLES.VENDOR_FE]: {
    dashboard: true,
    cases: false,
    users: false,
    clients: false,
    products: false,
    qc: false,
    reports: false,
    invoices: false,
    penalties: false,
    payouts: false,
    formBuilder: false,
    accessControl: false,
    statusTracker: false,
    notifications: false,
    vendorCreation: false,
    manualGeo: false,
    manualVerification: false,
    qualityCheck: false,
  },
};

export const AppProvider = ({ children }) => {
  // USER BASED ACCESS CONTROL
  // Since this is frontend only, we maintain a mock global map
  const [userPermissionsMap, setUserPermissionsMap] = useState({});
  const [currentUserId, setCurrentUserId] = useState(() => localStorage.getItem('crm_user_id') || "USR001");
  const [currentRole, setCurrentRole] = useState(() => localStorage.getItem('crm_role') || ROLES.ADMIN);
  const [isAuthenticated, setIsAuthenticated] = useState(() => localStorage.getItem('crm_auth') === 'true');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [rightDrawerOpen, setRightDrawerOpen] = useState(false);
  const [rightDrawerContent, setRightDrawerContent] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [usersInfo, setUsersInfo] = useState({});

  // Penalty and Waiver Management State
  const [penaltiesList, setPenaltiesList] = useState([]);
  const [waiveOffRequestsList, setWaiveOffRequestsList] = useState([]);
  
  // Case Deletion Requests State
  const [caseStatusOverrides, setCaseStatusOverrides] = useState({});
  const [deletionRequestsList, setDeletionRequestsList] = useState([]);

  // Shared state for Extra Cost Approvals
  const [extraCostsList, setExtraCostsList] = useState([]);

  // Shared state for Case Assignments (so FE can unassign cases globally)
  const [caseAssignments, setCaseAssignments] = useState({});
  const [caseReturnRequestsList, setCaseReturnRequestsList] = useState([]);
  const addNotification = useCallback((notification) => {
    const newNotification = {
      id: Date.now(),
      timestamp: new Date().toISOString(),
      read: false,
      ...notification,
    };
    setNotifications(prev => [newNotification, ...prev]);
  }, []);

  const markNotificationRead = useCallback((id) => {
    setNotifications(prev =>
      prev.map(n => n.id === id ? { ...n, read: true } : n)
    );
  }, []);

  const submitWaiveOffRequest = useCallback((req) => {
    setWaiveOffRequestsList(prev => [req, ...prev]);
    // Update penalty statuses
    setPenaltiesList(prev => prev.map(p => 
      req.penaltyIds.includes(p.id) ? { ...p, status: 'Waive Off Requested' } : p
    ));
    // Add notification
    addNotification({
      title: 'Waive Off Request Submitted',
      message: `Waive-off request submitted by ${req.requesterName} for ₹${req.totalAmount} penalty.`,
      type: 'info',
      targetRole: 'manager'
    });
  }, [addNotification]);

  const updateWaiveOffRecommendation = useCallback((reqId, role, recommendation, comment, userName) => {
    setWaiveOffRequestsList(prev => prev.map(req => {
      if (req.id !== reqId) return req;
      
      const newTimeline = req.timeline.map(step => {
        if (step.role === role) {
          return {
            ...step,
            action: `${recommendation === 'Approved' ? 'Recommended Approval' : 'Recommended Rejection'}`,
            user: userName,
            date: new Date().toISOString(),
            status: 'done',
            recommendation,
            comment
          };
        }
        // Enable next role as pending
        if (role === 'Manager' && step.role === 'Supreme Manager') {
          return { ...step, status: 'pending' };
        }
        if (role === 'Supreme Manager' && step.role === 'Admin') {
          return { ...step, status: 'pending' };
        }
        return step;
      });

      return {
        ...req,
        timeline: newTimeline
      };
    }));

    addNotification({
      title: `Waiver Recommendation by ${role}`,
      message: `${userName} recommended ${recommendation.toLowerCase()} for request ${reqId}.`,
      type: recommendation === 'Approved' ? 'success' : 'danger',
      targetRole: role === 'Manager' ? 'supreme_manager' : 'admin'
    });
  }, [addNotification]);

  const finalizeWaiveOffDecision = useCallback((reqId, decision, comment, userName) => {
    let targetRequest = null;
    
    setWaiveOffRequestsList(prev => prev.map(req => {
      if (req.id !== reqId) return req;
      targetRequest = req;

      const newTimeline = req.timeline.map(step => {
        if (step.role === 'Admin') {
          return {
            ...step,
            action: `${decision === 'Approved' ? 'Final Approved' : 'Final Rejected'}`,
            user: userName,
            date: new Date().toISOString(),
            status: 'done',
            recommendation: decision,
            comment
          };
        }
        return step;
      });

      return {
        ...req,
        status: decision,
        timeline: newTimeline
      };
    }));

    if (targetRequest) {
      const pStatus = decision === 'Approved' ? 'Waived' : 'Deducted';
      setPenaltiesList(prev => prev.map(p => 
        targetRequest.penaltyIds.includes(p.id) ? { ...p, status: pStatus } : p
      ));

      addNotification({
        title: `Waiver Request ${decision}`,
        message: `Your penalty waive-off request ${reqId} has been ${decision.toLowerCase()} by Admin ${userName}.`,
        type: decision === 'Approved' ? 'success' : 'danger',
        targetRole: 'field_executive'
      });
    }
  }, [addNotification]);

  const updateCaseStatus = useCallback((caseId, newStatus) => {
    setCaseStatusOverrides(prev => ({
      ...prev,
      [caseId]: newStatus
    }));
  }, []);

  const submitDeletionRequest = useCallback((req) => {
    setDeletionRequestsList(prev => [req, ...prev]);
    updateCaseStatus(req.caseId, 'delete_requested');

    // Add notification to managers/admins
    addNotification({
      title: 'Deletion Request Received',
      message: `Client has requested deletion for Case ${req.refNumber} (${req.applicantName}). Reason: ${req.reason}`,
      type: 'warning',
      targetRole: 'admin' // Visible to Admin, Supreme Manager
    });
    
    addNotification({
      title: 'Deletion Request Received',
      message: `Client has requested deletion for Case ${req.refNumber} (${req.applicantName}). Reason: ${req.reason}`,
      type: 'warning',
      targetRole: 'supreme_manager'
    });
    
    addNotification({
      title: 'Deletion Request Received',
      message: `Client has requested deletion for Case ${req.refNumber} (${req.applicantName}). Reason: ${req.reason}`,
      type: 'warning',
      targetRole: 'manager'
    });
  }, [addNotification, updateCaseStatus]);

  const approveDeletionRequest = useCallback((reqId, adminRemark, userName) => {
    let caseId = null;
    setDeletionRequestsList(prev => prev.map(req => {
      if (req.id !== reqId) return req;
      caseId = req.caseId;
      return {
        ...req,
        status: 'approved',
        adminRemark,
        approvedBy: userName,
        actionDate: new Date().toISOString()
      };
    }));

    if (caseId) {
      updateCaseStatus(caseId, 'delete_approved');
    }

    addNotification({
      title: 'Deletion Request Approved',
      message: `Deletion request for case has been approved by ${userName}. Remark: ${adminRemark}`,
      type: 'success',
      targetRole: 'client'
    });
  }, [addNotification, updateCaseStatus]);

  const rejectDeletionRequest = useCallback((reqId, adminRemark, userName) => {
    let caseId = null;
    setDeletionRequestsList(prev => prev.map(req => {
      if (req.id !== reqId) return req;
      caseId = req.caseId;
      return {
        ...req,
        status: 'rejected',
        adminRemark,
        rejectedBy: userName,
        actionDate: new Date().toISOString()
      };
    }));

    if (caseId) {
      updateCaseStatus(caseId, 'delete_rejected');
    }

    addNotification({
      title: 'Deletion Request Rejected',
      message: `Deletion request has been rejected by ${userName}. Remark: ${adminRemark}`,
      type: 'danger',
      targetRole: 'client'
    });
  }, [addNotification, updateCaseStatus]);

  useEffect(() => {
    // Optionally fetch active user preferences or global users here
    fetch('/backend_php/api/users.php')
      .then(res => res.json())
      .then(data => {
        const userMap = {};
        data.forEach(u => userMap[u.id] = u);
        setUsersInfo(userMap);
      })
      .catch(err => console.error("Could not fetch users", err));
  }, []);

  // Setup user permissions override
  const updatePermission = useCallback((userId, module, action, value) => {
    setUserPermissionsMap(prev => {
      const userPerms = prev[userId] || {};
      const modulePerms = typeof userPerms[module] === 'object' ? { ...userPerms[module] } : {};

      if (action) {
        modulePerms[action] = value;
        return { ...prev, [userId]: { ...userPerms, [module]: modulePerms } };
      } else {
        return { ...prev, [userId]: { ...userPerms, [module]: value } };
      }
    });
  }, []);

  const login = useCallback(() => {
    localStorage.setItem('crm_auth', 'true');
    setIsAuthenticated(true);
  }, []);
  const logout = useCallback(() => {
    localStorage.removeItem('crm_auth');
    localStorage.removeItem('crm_role');
    localStorage.removeItem('crm_user_id');
    setIsAuthenticated(false);
  }, []);

  useEffect(() => {
    if (currentRole) localStorage.setItem('crm_role', currentRole);
  }, [currentRole]);

  useEffect(() => {
    if (currentUserId) localStorage.setItem('crm_user_id', currentUserId);
  }, [currentUserId]);

  const hasPermission = useCallback((module, action = null) => {
    const rolePermissions = DEFAULT_ROLE_PERMISSIONS[currentRole];
    if (!rolePermissions) return false;

    let modulePermission = rolePermissions[module];

    // Check if user has an override
    const userOverrides = userPermissionsMap[currentUserId];
    if (userOverrides && userOverrides[module] !== undefined) {
      if (action && typeof userOverrides[module] === 'object' && userOverrides[module][action] !== undefined) {
        return userOverrides[module][action];
      }
      if (!action && typeof userOverrides[module] === 'boolean') {
        return userOverrides[module];
      }
    }

    if (typeof modulePermission === 'boolean') return modulePermission;
    if (action && typeof modulePermission === 'object') {
      return modulePermission[action] || false;
    }
    return !!modulePermission;
  }, [currentRole, currentUserId, userPermissionsMap]);

  const openRightDrawer = useCallback((content) => {
    setRightDrawerContent(content);
    setRightDrawerOpen(true);
  }, []);

  const closeRightDrawer = useCallback(() => {
    setRightDrawerOpen(false);
    setTimeout(() => setRightDrawerContent(null), 300);
  }, []);
  const submitExtraCostRequest = useCallback((req) => {
    setExtraCostsList(prev => [req, ...prev]);
    // Notify Manager, Supreme Manager, and Admin
    addNotification({
      title: 'New Extra Cost Request',
      message: `${req.requestedBy.name} requested ₹${req.amount} for Case ${req.caseRef}.`,
      type: 'info',
      targetRole: 'manager'
    });
    addNotification({
      title: 'New Extra Cost Request',
      message: `${req.requestedBy.name} requested ₹${req.amount} for Case ${req.caseRef}.`,
      type: 'info',
      targetRole: 'supreme_manager'
    });
    addNotification({
      title: 'New Extra Cost Request',
      message: `${req.requestedBy.name} requested ₹${req.amount} for Case ${req.caseRef}.`,
      type: 'info',
      targetRole: 'admin'
    });
  }, [addNotification]);

  const updateExtraCostStatus = useCallback((id, status) => {
    setExtraCostsList(prev => {
      const updated = prev.map(r => r.id === id ? { ...r, status } : r);
      const req = updated.find(r => r.id === id);
      if (req) {
        let notificationTitle = `Extra Cost Request Update`;
        let notificationMsg = `Request ${id} status updated to ${status}.`;
        let type = 'info';
        let targetRole = 'field_executive';

        if (status === 'recommended_manager') {
          notificationTitle = `Extra Cost Recommended by Manager`;
          notificationMsg = `Manager recommended approval for extra cost request ${id} (₹${req.amount}).`;
          type = 'success';
          targetRole = 'supreme_manager';
        } else if (status === 'recommended_supreme') {
          notificationTitle = `Extra Cost Recommended by Supreme Manager`;
          notificationMsg = `Supreme Manager recommended approval for extra cost request ${id} (₹${req.amount}).`;
          type = 'success';
          targetRole = 'admin';
        } else if (status === 'approved') {
          notificationTitle = `Extra Cost Approved`;
          notificationMsg = `Your extra cost request of ₹${req.amount} for Case ${req.caseRef} has been approved.`;
          type = 'success';
          targetRole = 'field_executive';
        } else if (status === 'rejected') {
          notificationTitle = `Extra Cost Rejected`;
          notificationMsg = `Your extra cost request of ₹${req.amount} for Case ${req.caseRef} has been rejected.`;
          type = 'danger';
          targetRole = 'field_executive';
        }

        addNotification({
          title: notificationTitle,
          message: notificationMsg,
          type: type,
          targetRole: targetRole
        });
      }
      return updated;
    });
  }, [addNotification]);

  const rejectCaseNonWorkingArea = useCallback((caseId, userName) => {
    // 1. Unassign case by mapping its assignedTo to null override
    setCaseAssignments(prev => ({
      ...prev,
      [caseId]: 'unassigned' // flag to filter it out or show as unassigned
    }));
    
    // 2. Set status to created (superiors unassigned pool)
    setCaseStatusOverrides(prev => ({
      ...prev,
      [caseId]: 'created'
    }));

    // 3. Notify superiors
    addNotification({
      title: 'Case Rejected: Non-Working Area',
      message: `Field Executive ${userName} rejected Case ${caseId} due to Non-Working Area. Case has been returned to the unassigned pool.`,
      type: 'warning',
      targetRole: 'manager'
    });
  }, [addNotification]);

  const submitReturnRequest = useCallback((req) => {
    setCaseReturnRequestsList(prev => [req, ...prev]);
    // Flag the case as return pending
    setCaseStatusOverrides(prev => ({
      ...prev,
      [req.caseId]: 'return_pending'
    }));
    // Notify Manager, Supreme Manager, and Admin
    addNotification({
      title: 'Case Return Requested',
      message: `Field Executive ${req.requestedBy.name} requested case return for ${req.refNumber} due to ${req.reason}.`,
      type: 'warning',
      targetRole: 'manager'
    });
    addNotification({
      title: 'Case Return Requested',
      message: `Field Executive ${req.requestedBy.name} requested case return for ${req.refNumber} due to ${req.reason}.`,
      type: 'warning',
      targetRole: 'supreme_manager'
    });
    addNotification({
      title: 'Case Return Requested',
      message: `Field Executive ${req.requestedBy.name} requested case return for ${req.refNumber} due to ${req.reason}.`,
      type: 'warning',
      targetRole: 'admin'
    });
  }, [addNotification]);

  const recommendReturnRequest = useCallback((reqId, role, decision, comment, userName) => {
    setCaseReturnRequestsList(prev => prev.map(req => {
      if (req.id !== reqId) return req;
      return {
        ...req,
        status: decision === 'Approved' ? 'recommended' : 'rejected',
        timeline: [
          ...req.timeline,
          { role, action: decision === 'Approved' ? 'Recommended Return' : 'Rejected Return', user: userName, date: new Date().toISOString().split('T')[0], comment }
        ]
      };
    }));

    const reqItem = caseReturnRequestsList.find(r => r.id === reqId);

    if (decision === 'Rejected' && reqItem) {
      // Revert case status override to assigned
      setCaseStatusOverrides(prev => {
        const copy = { ...prev };
        delete copy[reqItem.caseId];
        return copy;
      });
    }

    addNotification({
      title: `Case Return ${decision === 'Approved' ? 'Recommended' : 'Rejected'}`,
      message: `${userName} (${role}) ${decision === 'Approved' ? 'recommended' : 'rejected'} return for request ${reqId}.`,
      type: decision === 'Approved' ? 'info' : 'danger',
      targetRole: decision === 'Approved' ? 'admin' : 'field_executive'
    });
  }, [addNotification, caseReturnRequestsList]);

  const approveReturnRequest = useCallback((reqId, adminRemark, userName) => {
    let targetReq = null;
    setCaseReturnRequestsList(prev => prev.map(req => {
      if (req.id !== reqId) return req;
      targetReq = req;
      return {
        ...req,
        status: 'approved',
        adminRemark,
        timeline: [
          ...req.timeline,
          { role: 'Admin', action: 'Approved Return', user: userName, date: new Date().toISOString().split('T')[0], comment: adminRemark }
        ]
      };
    }));

    if (targetReq) {
      // Revert case back to created status, and clear assignee (assignedTo = null)
      updateCaseStatus(targetReq.caseId, 'created');
      
      setCaseAssignments(prev => ({
        ...prev,
        [targetReq.caseId]: 'unassigned' // unassign case
      }));

      addNotification({
        title: 'Case Return Approved',
        message: `Case return request for ${targetReq.refNumber} was approved by Admin. Case returned to pool.`,
        type: 'success',
        targetRole: 'field_executive'
      });
    }
  }, [addNotification, updateCaseStatus, setCaseAssignments]);

  const rejectReturnRequest = useCallback((reqId, adminRemark, userName) => {
    let targetReq = null;
    setCaseReturnRequestsList(prev => prev.map(req => {
      if (req.id !== reqId) return req;
      targetReq = req;
      return {
        ...req,
        status: 'rejected',
        adminRemark,
        timeline: [
          ...req.timeline,
          { role: 'Admin', action: 'Rejected Return', user: userName, date: new Date().toISOString().split('T')[0], comment: adminRemark }
        ]
      };
    }));

    if (targetReq) {
      // Revert case status override
      setCaseStatusOverrides(prev => {
        const copy = { ...prev };
        delete copy[targetReq.caseId];
        return copy;
      });

      // Clear caseAssignments override if present
      setCaseAssignments(prev => {
        const copy = { ...prev };
        delete copy[targetReq.caseId];
        return copy;
      });

      addNotification({
        title: 'Case Return Rejected',
        message: `Case return request for ${targetReq.refNumber} was rejected by Admin. Please resume verification.`,
        type: 'danger',
        targetRole: 'field_executive'
      });
    }
  }, [addNotification, setCaseAssignments]);
  const value = {
    isAuthenticated,
    login,
    logout,
    currentUserId,
    setCurrentUserId,
    currentRole,
    setCurrentRole,
    userPermissionsMap,
    updatePermission,
    sidebarCollapsed,
    setSidebarCollapsed,
    rightDrawerOpen,
    rightDrawerContent,
    openRightDrawer,
    closeRightDrawer,
    hasPermission,
    notifications,
    addNotification,
    markNotificationRead,
    unreadCount: notifications.filter(n => !n.read).length,
    usersInfo,
    penaltiesList,
    setPenaltiesList,
    waiveOffRequestsList,
    setWaiveOffRequestsList,
    submitWaiveOffRequest,
    updateWaiveOffRecommendation,
    finalizeWaiveOffDecision,
    caseStatusOverrides,
    updateCaseStatus,
    deletionRequestsList,
    submitDeletionRequest,
    approveDeletionRequest,
    rejectDeletionRequest,
    extraCostsList,
    setExtraCostsList,
    submitExtraCostRequest,
    updateExtraCostStatus,
    caseAssignments,
    setCaseAssignments,
    rejectCaseNonWorkingArea,
    caseReturnRequestsList,
    setCaseReturnRequestsList,
    submitReturnRequest,
    recommendReturnRequest,
    approveReturnRequest,
    rejectReturnRequest,
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};

export default AppContext;
